import React, { useMemo, useRef, useState } from 'react'
import * as Immer from 'immer'
import classNames from 'classnames'
import moment from 'moment'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Button } from 'primereact/button'
import { Toolbar } from 'primereact/toolbar'
import { Dialog } from 'primereact/dialog'
import { InputText } from 'primereact/inputtext'
import { InputNumber } from 'primereact/inputnumber'
import { InputTextarea } from 'primereact/inputtextarea'
import { Calendar } from 'primereact/calendar'
import { Dropdown } from 'primereact/dropdown'
import { IconField } from 'primereact/iconfield'
import { Checkbox } from 'primereact/checkbox'
import { InputIcon } from 'primereact/inputicon'
import { Skeleton } from 'primereact/skeleton'
import { TabPanel, TabView } from 'primereact/tabview'
import { TreeSelect } from 'primereact/treeselect'
import { MultiSelect } from 'primereact/multiselect'
import { Tag } from 'primereact/tag'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  listContractsRequest,
  createContractRequest,
  patchContractRequest,
  deleteContractRequest,
  listAllCompaniesRequest,
  listAllUsersRequest,
  listProductsRequest,
  listAllScalesRequest,
  listAllDevicesRequest,
  listAllWaterMeterRequest,
} from '../apis/calyxSaaSDashboardAPI'
import { useApiErrorHandler } from '../hooks/useApiErrorHandler'
import { useToast } from '../hooks/useToast'
import { formatUTCDate } from '../utils/format'
import { calendarValueToUTC, nowTruncatedToDay, nowTruncatedToMinute, utcToCalendarValue } from '../utils/dateUtils'
import type { ContractResBodyDto, CreateContractReqBodyDto, PatchContractReqBodyDto, ProductResBodyDto, ShippingRecordResBodyDto } from '../apis/hey-api'

// --- Types ---

type LineItemFormData = {
  _formKey: number
  productId: string | null
  quantity: number | null
  associatedHouseIds: string[]
  associatedScaleIds: string[]
  associatedDeviceIds: string[]
  associatedWaterMeterIds: string[]
  memo: string | null
}

type ShippingRecordFormData = {
  _formKey: number
  shippingType: 'TO_WAREHOUSE' | 'TO_CUSTOMER' | null
  shippingProviderType: 'FEDEX' | 'T_CAT' | null
  trackingNumber: string
  shippingStatus: 'PENDING' | 'TRACKING' | 'DELIVERED' | 'RETURNED' | 'CANCELLED' | 'OTHER'
  deliveredTime: string | null
  memo: string | null
}

type ContractFormData = {
  id?: string
  companyId: string | null
  businessId: string | null
  quoteNo: string | null
  quoteUrl: string | null
  invoiceNo: string | null
  invoiceUrl: string | null
  lineItems: LineItemFormData[]
  subscriptionPeriodCount: number | null
  subscriptionPeriodUnit: 'YEAR' | 'MONTH' | 'DAY'
  activationDate: string | null
  expirationDate: string | null
  paymentCurrency: string
  amountDue: number | null
  amountPaid: number
  shippingRecords: ShippingRecordFormData[]
  isResolved: boolean
  memo: string | null
  _revision?: number
}

type EnrichedContract = ContractResBodyDto & {
  companyName: string | undefined
  businessName: string | undefined
  creatorName: string | undefined
}

// --- Constants ---

const makeEmptyContract = (): ContractFormData => ({
  companyId: null,
  businessId: null,
  quoteNo: null,
  quoteUrl: null,
  invoiceNo: null,
  invoiceUrl: null,
  lineItems: [],
  subscriptionPeriodCount: null,
  subscriptionPeriodUnit: 'YEAR',
  activationDate: null,
  expirationDate: null,
  paymentCurrency: localStorage.getItem('contracts.lastPaymentCurrency') ?? 'USD',
  amountDue: null,
  amountPaid: 0,
  shippingRecords: [],
  isResolved: false,
  memo: null,
})

const DURATION_UNIT_LABELS: Record<string, string> = { YEAR: 'year', MONTH: 'month', DAY: 'day' }

const PAYMENT_CURRENCY_OPTIONS = [
  { label: 'USD', value: 'USD' },
  { label: 'TWD', value: 'TWD' },
]

const SHIPPING_TYPE_OPTIONS = [
  { label: 'To Warehouse', value: 'TO_WAREHOUSE' },
  { label: 'To Customer', value: 'TO_CUSTOMER' },
]

const SHIPPING_PROVIDER_TYPE_OPTIONS = [
  { label: 'FedEx', value: 'FEDEX' },
  { label: 'T-Cat', value: 'T_CAT' },
]

const SUBSCRIPTION_PERIOD_UNIT_OPTIONS = [
  { label: 'Year', value: 'YEAR' },
  { label: 'Month', value: 'MONTH' },
  { label: 'Day', value: 'DAY' },
]

const SHIPPING_STATUS_OPTIONS = [
  { label: 'Pending', value: 'PENDING' },
  { label: 'Tracking', value: 'TRACKING' },
  { label: 'Delivered', value: 'DELIVERED' },
  { label: 'Returned', value: 'RETURNED' },
  { label: 'Cancelled', value: 'CANCELLED' },
  { label: 'Other', value: 'OTHER' },
]

const buildTreeSelectionValue = (companies: any[], selectedHouseIds: string[]) => {
  const selectedHouseIdSet = new Set(selectedHouseIds)
  const result: Record<string, { checked: boolean; partialChecked: boolean }> = {}

  for (const company of companies) {
    let companyTotal = 0
    let companyChecked = 0

    for (const business of company.businesses) {
      let businessTotal = 0
      let businessChecked = 0

      for (const farm of business.farms) {
        let farmTotal = 0
        let farmChecked = 0

        for (const house of farm.houses) {
          const isHouseSelected = selectedHouseIdSet.has(house.id)

          if (isHouseSelected) {
            result[`house/${house.id}`] = {
              checked: true,
              partialChecked: false,
            }
          }

          farmTotal += 1
          farmChecked += isHouseSelected ? 1 : 0
        }

        if (farmChecked > 0) {
          result[`farm/${farm.id}`] = {
            checked: farmChecked === farmTotal,
            partialChecked: farmChecked < farmTotal,
          }
        }

        businessTotal += farmTotal
        businessChecked += farmChecked
      }

      if (businessChecked > 0) {
        result[`business/${business.id}`] = {
          checked: businessChecked === businessTotal,
          partialChecked: businessChecked < businessTotal,
        }
      }

      companyTotal += businessTotal
      companyChecked += businessChecked
    }

    if (companyChecked > 0) {
      result[`company/${company.id}`] = {
        checked: companyChecked === companyTotal,
        partialChecked: companyChecked < companyTotal,
      }
    }
  }

  return result
}

const MONO_MULTI_SELECT_PT = {
  label: { style: { display: 'flex', flexWrap: 'wrap', gap: '4px' } },
  token: { style: { margin: '0', fontFamily: 'monospace' } },
  item: { style: { fontFamily: 'monospace' } },
}

// --- Query Keys ---

const LIST_CONTRACTS_QUERY_KEY = ['list', 'contracts'] as const
const LIST_COMPANIES_QUERY_KEY = ['list', 'companies'] as const
const LIST_USERS_QUERY_KEY = ['list', 'users'] as const
const LIST_PRODUCTS_QUERY_KEY = ['list', 'products'] as const
const LIST_SCALES_QUERY_KEY = ['list', 'scales'] as const
const LIST_DEVICES_QUERY_KEY = ['list', 'devices'] as const
const LIST_WATER_METERS_QUERY_KEY = ['list', 'waterMeters'] as const

// --- Component ---

export const CrudContracts = () => {
  const queryClient = useQueryClient()
  const toast = useToast()
  const formKeyRef = useRef(0)
  const generateFormKey = () => formKeyRef.current++
  const handleError = useApiErrorHandler()

  // --- State ---

  const [createOrEditDialogVisible, setCreateOrEditDialogVisible] = useState(false)
  const [isCreateMode, setIsCreateMode] = useState(false)
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false)
  const [focusedContract, setFocusedContract] = useState<ContractFormData>(() => makeEmptyContract())
  const [globalFilter, setGlobalFilter] = useState<string>('')

  // --- Queries ---

  const listContractsQuery = useQuery<ContractResBodyDto[]>(
    LIST_CONTRACTS_QUERY_KEY,
    () => listContractsRequest(),
    { onError: handleError },
  )

  const listCompaniesQuery = useQuery<any[]>(
    LIST_COMPANIES_QUERY_KEY,
    () => listAllCompaniesRequest(),
    { onError: handleError },
  )

  const listUsersQuery = useQuery<any[]>(
    LIST_USERS_QUERY_KEY,
    () => listAllUsersRequest(),
    { onError: handleError },
  )

  const listProductsQuery = useQuery<ProductResBodyDto[]>(
    LIST_PRODUCTS_QUERY_KEY,
    () => listProductsRequest(),
    { onError: handleError },
  )

  const listScalesQuery = useQuery<any[]>(
    LIST_SCALES_QUERY_KEY,
    () => listAllScalesRequest(),
    { onError: handleError },
  )

  const listDevicesQuery = useQuery<any[]>(
    LIST_DEVICES_QUERY_KEY,
    () => listAllDevicesRequest(),
    { onError: handleError },
  )

  const listWaterMetersQuery = useQuery<any[]>(
    LIST_WATER_METERS_QUERY_KEY,
    () => listAllWaterMeterRequest(),
    { onError: handleError },
  )

  // --- Maps for name resolution ---

  const companyMap = useMemo(() => {
    return new Map(listCompaniesQuery.data?.map((company) => [company.id, company]) ?? [])
  }, [listCompaniesQuery.data])

  const userMap = useMemo(() => {
    return new Map(listUsersQuery.data?.map((user) => [user.id, user]) ?? [])
  }, [listUsersQuery.data])

  const productMap = useMemo(() => {
    return new Map(listProductsQuery.data?.map((product) => [product.id, product]) ?? [])
  }, [listProductsQuery.data])

  // --- Enriched contracts ---

  const enrichedContracts = useMemo<EnrichedContract[] | undefined>(() => {
    if (listContractsQuery.data == null) return undefined

    return listContractsQuery.data
      .map((contract) => {
        const company: any | undefined = companyMap.get(contract.companyId)
        const businesses: any[] | undefined = company?.businesses
        const business: any | undefined = businesses?.find((business) => business.id === contract.businessId)
        const user: any | undefined = userMap.get(contract.creatorId)

        return {
          ...contract,
          companyName: company?.name,
          businessName: business?.name,
          creatorName: user != null
            ? `${user.firstname} ${user.lastname}`
            : undefined,
        }
      })
      .sort((a, b) => new Date(b.createdTime).getTime() - new Date(a.createdTime).getTime())
  }, [listContractsQuery.data, companyMap, userMap])

  // --- Dropdown options ---

  const companyDropdownOptions = useMemo(() => {
    return listCompaniesQuery.data?.map((company) => ({
      label: company.name,
      value: company.id,
    })) ?? []
  }, [listCompaniesQuery.data])

  const productDropdownOptions = useMemo(() => {
    return listProductsQuery.data?.map((product) => ({
      label: product.name,
      value: product.id,
    })) ?? []
  }, [listProductsQuery.data])

  const houseTreeOptions = useMemo(() => {
    const companies = listCompaniesQuery.data ?? []
    const countMap = new Map<string, number>()

    for (const company of companies) {
      let companyCount = 0

      for (const business of company.businesses) {
        let businessCount = 0

        for (const farm of business.farms) {
          const farmCount = farm.houses.length
          countMap.set(farm.id, farmCount)
          businessCount += farmCount
        }

        countMap.set(business.id, businessCount)
        companyCount += businessCount
      }

      countMap.set(company.id, companyCount)
    }

    return companies.map((company: any) => ({
      key: `company/${company.id}`,
      label: `${company.name} (${countMap.get(company.id) ?? 0})`,
      icon: 'pi pi-fw pi-building',
      children: company.businesses.map((business: any) => ({
        key: `business/${business.id}`,
        label: `${business.name ?? `(${business.type})`} (${countMap.get(business.id) ?? 0})`,
        icon: 'pi pi-fw pi-briefcase',
        children: business.farms.map((farm: any) => ({
          key: `farm/${farm.id}`,
          label: `${farm.name} (${countMap.get(farm.id) ?? 0})`,
          icon: 'pi pi-fw pi-warehouse',
          children: farm.houses.map((house: any) => ({
            key: `house/${house.id}`,
            label: house.name,
            icon: 'pi pi-fw pi-home',
          })),
        })),
      })),
    }))
  }, [listCompaniesQuery.data])

  const houseChipLabelMap = useMemo(() => {
    const companies = listCompaniesQuery.data ?? []
    const map = new Map<string, string[]>()
    for (const company of companies) {
      for (const business of company.businesses) {
        for (const farm of business.farms) {
          for (const house of farm.houses) {
            map.set(house.id, [farm.name, house.name])
          }
        }
      }
    }
    return map
  }, [listCompaniesQuery.data])

  const scaleMultiSelectOptions = useMemo(() => {
    return listScalesQuery.data
      ?.map((scale: any) => ({
        label: scale.serialNumber,
        value: scale.id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label)) ?? []
  }, [listScalesQuery.data])

  const deviceMultiSelectOptions = useMemo(() => {
    return listDevicesQuery.data
      ?.map((device: any) => ({
        label: `${device.serialNumber} [${device.hardwareSerial}]`,
        value: device.id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label)) ?? []
  }, [listDevicesQuery.data])

  const waterMeterMultiSelectOptions = useMemo(() => {
    return listWaterMetersQuery.data
      ?.map((waterMeter: any) => ({
        label: `${waterMeter.serialNumber} [${waterMeter.hardwareSerial}]`,
        value: waterMeter.id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label)) ?? []
  }, [listWaterMetersQuery.data])

  const businessDropdownOptions = useMemo(() => {
    if (focusedContract.companyId == null) return []
    const company = companyMap.get(focusedContract.companyId)
    const businesses = company?.businesses ?? []

    return businesses.map((business: any) => ({
      label: (business.name ?? business.type) as string,
      value: business.id as string,
    }))
  }, [focusedContract.companyId, companyMap])

  // --- Mutations ---

  const createContractMutation = useMutation(
    ({ body }: { body: CreateContractReqBodyDto }) => createContractRequest(body),
    {
      onSuccess: (newContract) => {
        queryClient.setQueryData<ContractResBodyDto[]>(
          LIST_CONTRACTS_QUERY_KEY,
          (contracts) => contracts?.concat(newContract)
        )

        toast.current?.show({ severity: 'success', summary: 'Success', detail: 'New contract has been saved', life: 3000 })

        hideCreateOrEditDialog()
      },
      onError: handleError,
    },
  )

  const patchContractMutation = useMutation(
    ({ contractId, body }: { contractId: string; body: PatchContractReqBodyDto }) => patchContractRequest(contractId, body),
    {
      onSuccess: (updatedContract, { contractId }) => {
        queryClient.setQueryData<ContractResBodyDto[]>(
          LIST_CONTRACTS_QUERY_KEY,
          (contracts) => contracts?.map((contract) => (contract.id === contractId ? updatedContract : contract))
        )

        toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Contract has been updated', life: 3000 })

        hideCreateOrEditDialog()
      },
      onError: handleError,
    },
  )

  const deleteContractMutation = useMutation(
    ({ contractId }: { contractId: string }) => deleteContractRequest(contractId),
    {
      onSuccess: (_, { contractId }) => {
        queryClient.setQueryData<ContractResBodyDto[]>(
          LIST_CONTRACTS_QUERY_KEY,
          (contracts) => contracts?.filter((contract) => contract.id !== contractId)
        )

        toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Contract has been deleted', life: 3000 })

        hideDeleteDialog()
      },
      onError: handleError,
    },
  )

  // --- Dialog helpers ---

  const openCreateDialog = () => {
    setIsCreateMode(true)

    setFocusedContract(makeEmptyContract())

    setCreateOrEditDialogVisible(true)
  }

  const contractToFormData = (contract: EnrichedContract): ContractFormData => ({
    id: contract.id,
    companyId: contract.companyId,
    businessId: contract.businessId,
    quoteNo: contract.quoteNo,
    quoteUrl: contract.quoteUrl,
    invoiceNo: contract.invoiceNo,
    invoiceUrl: contract.invoiceUrl,
    lineItems: contract.lineItems.map((lineItem) => {
      const grouped = new Map<string, typeof lineItem.associatedObjects>()
      for (const associatedObject of lineItem.associatedObjects) {
        const key = associatedObject.objectType
        if (!grouped.has(key)) grouped.set(key, [])
        grouped.get(key)!.push(associatedObject)
      }

      return {
        _formKey: generateFormKey(),
        productId: lineItem.productId,
        quantity: lineItem.quantity,
        associatedHouseIds: grouped.get('HOUSE')?.map((associatedObject) => associatedObject.objectId) ?? [],
        associatedScaleIds: grouped.get('SCALE')?.map((associatedObject) => associatedObject.objectId) ?? [],
        associatedDeviceIds: grouped.get('CALYX_Y')?.map((associatedObject) => associatedObject.objectId) ?? [],
        associatedWaterMeterIds: grouped.get('WATER_METER')?.map((associatedObject) => associatedObject.objectId) ?? [],
        memo: lineItem.memo,
      }
    }),
    subscriptionPeriodCount: contract.subscriptionPeriod?.durationCount ?? null,
    subscriptionPeriodUnit: contract.subscriptionPeriod?.durationUnit ?? 'YEAR',
    activationDate: contract.activationDate,
    expirationDate: contract.expirationDate,
    paymentCurrency: contract.paymentCurrency,
    amountDue: contract.amountDue,
    amountPaid: contract.amountPaid,
    shippingRecords: contract.shippingRecords.map((record) => ({
      _formKey: generateFormKey(),
      shippingType: record.shippingType,
      shippingProviderType: record.shippingProviderType,
      trackingNumber: record.trackingNumber,
      shippingStatus: record.shippingStatus,
      deliveredTime: record.deliveredTime,
      memo: record.memo,
    })),
    isResolved: contract.isResolved,
    memo: contract.memo,
    _revision: contract._revision,
  })

  const openEditDialog = (contract: EnrichedContract) => {
    setIsCreateMode(false)
    setFocusedContract(contractToFormData(contract))
    setCreateOrEditDialogVisible(true)
  }

  const hideCreateOrEditDialog = () => {
    setCreateOrEditDialogVisible(false)
  }

  const openDeleteDialog = (contract: EnrichedContract) => {
    setFocusedContract(contractToFormData(contract))
    setDeleteDialogVisible(true)
  }

  const hideDeleteDialog = () => {
    setDeleteDialogVisible(false)
  }

  // --- Actions ---

  const createContract = () => {
    if (!focusedContract.companyId || focusedContract.amountDue == null) {
      toast.current?.show({ severity: 'error', summary: 'Validation Error', detail: 'Please fill in required fields before apply.', sticky: true })
      return
    }

    if (focusedContract.subscriptionPeriodCount != null && focusedContract.subscriptionPeriodCount <= 0) {
      toast.current?.show({ severity: 'error', summary: 'Validation Error', detail: 'Subscription period must be greater than 0 if specified.', sticky: true })
      return
    }

    createContractMutation.mutate({
      body: {
        companyId: focusedContract.companyId,
        businessId: focusedContract.businessId,
        quoteNo: focusedContract.quoteNo,
        quoteUrl: focusedContract.quoteUrl,
        invoiceNo: focusedContract.invoiceNo,
        invoiceUrl: focusedContract.invoiceUrl,
        lineItems: [],
        subscriptionPeriod: focusedContract.subscriptionPeriodCount != null
          ? { durationCount: focusedContract.subscriptionPeriodCount, durationUnit: focusedContract.subscriptionPeriodUnit }
          : null,
        activationDate: focusedContract.activationDate,
        expirationDate: focusedContract.expirationDate,
        paymentCurrency: focusedContract.paymentCurrency,
        amountDue: focusedContract.amountDue!,
        amountPaid: focusedContract.amountPaid,
        shippingRecords: [],
        isResolved: focusedContract.isResolved,
        memo: focusedContract.memo,
      },
    })
  }

  const editContract = () => {
    if (!focusedContract.companyId || focusedContract.amountDue == null) {
      toast.current?.show({ severity: 'error', summary: 'Validation Error', detail: 'Please fill in required fields before apply.', sticky: true })
      return
    }

    if (focusedContract.subscriptionPeriodCount != null && focusedContract.subscriptionPeriodCount <= 0) {
      toast.current?.show({ severity: 'error', summary: 'Validation Error', detail: 'Subscription period must be greater than 0 if specified.', sticky: true })
      return
    }

    const hasInvalidLineItems = focusedContract.lineItems.some(
      (lineItem) => lineItem.productId == null || lineItem.quantity == null || lineItem.quantity < 1
    )

    if (hasInvalidLineItems) {
      toast.current?.show({ severity: 'error', summary: 'Validation Error', detail: 'Each line item must have a product and quantity (>= 1).', sticky: true })
      return
    }

    const hasInvalidShippingRecords = focusedContract.shippingRecords.some(
      (record) => !record.shippingType || !record.shippingProviderType || !record.trackingNumber.trim()
    )

    if (hasInvalidShippingRecords) {
      toast.current?.show({ severity: 'error', summary: 'Validation Error', detail: 'Each shipping record must have a type, provider, and tracking number.', sticky: true })
      return
    }

    patchContractMutation.mutate({
      contractId: focusedContract.id!,
      body: {
        companyId: focusedContract.companyId,
        businessId: focusedContract.businessId,
        quoteNo: focusedContract.quoteNo,
        quoteUrl: focusedContract.quoteUrl,
        invoiceNo: focusedContract.invoiceNo,
        invoiceUrl: focusedContract.invoiceUrl,
        lineItems: focusedContract.lineItems.map((lineItem) => ({
          productId: lineItem.productId!,
          quantity: lineItem.quantity!,
          associatedObjects: [
            ...lineItem.associatedHouseIds.map((objectId) => ({ objectType: 'HOUSE' as const, objectId })),
            ...lineItem.associatedScaleIds.map((objectId) => ({ objectType: 'SCALE' as const, objectId })),
            ...lineItem.associatedDeviceIds.map((objectId) => ({ objectType: 'CALYX_Y' as const, objectId })),
            ...lineItem.associatedWaterMeterIds.map((objectId) => ({ objectType: 'WATER_METER' as const, objectId })),
          ],
          memo: lineItem.memo,
        })),
        subscriptionPeriod: focusedContract.subscriptionPeriodCount != null
          ? { durationCount: focusedContract.subscriptionPeriodCount, durationUnit: focusedContract.subscriptionPeriodUnit }
          : null,
        activationDate: focusedContract.activationDate,
        expirationDate: focusedContract.expirationDate,
        paymentCurrency: focusedContract.paymentCurrency,
        amountDue: focusedContract.amountDue!,
        amountPaid: focusedContract.amountPaid,
        shippingRecords: focusedContract.shippingRecords.map((record) => ({
          shippingType: record.shippingType!,
          shippingProviderType: record.shippingProviderType!,
          trackingNumber: record.trackingNumber,
          shippingStatus: record.shippingStatus,
          deliveredTime: record.deliveredTime,
          memo: record.memo,
        })),
        isResolved: focusedContract.isResolved,
        memo: focusedContract.memo,
        _revision: focusedContract._revision,
      },
    })
  }

  const deleteContract = () => {
    deleteContractMutation.mutate({ contractId: focusedContract.id! })
  }

  // --- Templates ---

  const invoiceNumberBodyTemplate = (rowData: EnrichedContract) => {
    const { companyName, businessName } = rowData

    return (
      <>
        <span className="p-column-title">Invoice Number</span>
        <div className="flex flex-column align-items-start gap-2">
          <div className="flex flex-wrap align-items-center gap-1 text-sm text-gray-500">
            {[companyName, businessName].filter((name) => name != null).map((name, index) => (
              <span className="flex align-items-center white-space-nowrap gap-1" key={index}>
                {index !== 0 && <i className="pi pi-angle-right" />}
                <span>{name}</span>
              </span>
            ))}
          </div>
          <span className="flex align-items-center gap-2">
            <code className={classNames('text-lg', { 'text-color-secondary': rowData.invoiceNo == null })}>{rowData.invoiceNo ?? '—'}</code>
            {rowData.invoiceUrl != null && (
              <a href={rowData.invoiceUrl} target="_blank" rel="noopener noreferrer">
                <i className="pi pi-fw pi-link text-blue-500" />
              </a>
            )}
          </span>
          {rowData.creatorName != null ? (
            <Tag value={rowData.creatorName} severity="info" icon="pi pi-user" style={{ fontSize: '0.75rem' }} />
          ) : (
            <Tag value="Unknown User" severity="warning" icon="pi pi-user" style={{ fontSize: '0.75rem' }} />
          )}
        </div>
      </>
    )
  }

  const formatPeriod = (period: { durationCount: number; durationUnit: string }) => {
    const unit = DURATION_UNIT_LABELS[period.durationUnit] ?? period.durationUnit
    return `${period.durationCount} ${unit}${period.durationCount > 1 ? 's' : ''}`
  }

  const activationDateBodyTemplate = (rowData: EnrichedContract) => (
    <>
      <span className="p-column-title">Activation Date</span>
      <div className="flex flex-column gap-2">
        {rowData.activationDate != null ? (
          <span>{formatUTCDate(new Date(rowData.activationDate))}</span>
        ) : (
          <span className="text-color-secondary">N/A</span>
        )}
        {rowData.subscriptionPeriod != null && (
          <span className="text-sm text-blue-500">
            [{formatPeriod(rowData.subscriptionPeriod)}]
          </span>
        )}
      </div>
    </>
  )

  const expirationDateBodyTemplate = (rowData: EnrichedContract) => (
    <>
      <span className="p-column-title">Expiration Date</span>
      <div className="flex flex-column gap-2">
        {rowData.expirationDate != null ? (
          <span>{formatUTCDate(new Date(rowData.expirationDate))}</span>
        ) : (
          <span className="text-color-secondary">N/A</span>
        )}
        {rowData.expirationDate != null && (() => {
          const remainingDays = moment(rowData.expirationDate).diff(moment(), 'days')

          if (remainingDays > 0) {
            return <span className="text-sm text-green-500">({remainingDays} days remaining)</span>
          } else if (remainingDays < 0) {
            return <span className="text-sm text-red-500">(expired {Math.abs(remainingDays)} days ago)</span>
          } else {
            return <span className="text-sm text-orange-500">(expires today)</span>
          }
        })()}
      </div>
    </>
  )

  const paymentBodyTemplate = (rowData: EnrichedContract) => {
    const { amountDue, amountPaid, paymentCurrency } = rowData

    const { icon, label, colorClass, diff } = (() => {
      if (amountPaid > amountDue) {
        return { icon: 'pi pi-info-circle', label: 'Overpaid', colorClass: 'text-blue-500', diff: `+${amountPaid - amountDue} ${paymentCurrency}` }
      } else if (amountPaid < amountDue) {
        return amountPaid > 0
          ? { icon: 'pi pi-info-circle', label: 'Underpaid', colorClass: 'text-orange-500', diff: `-${amountDue - amountPaid} ${paymentCurrency}` }
          : { icon: 'pi pi-exclamation-circle', label: 'Unpaid', colorClass: 'text-red-500', diff: null }
      } else {
        return amountDue > 0
          ? { icon: 'pi pi-check-circle', label: 'Paid', colorClass: 'text-green-500', diff: null }
          : { icon: 'pi pi-minus-circle', label: 'Not Required', colorClass: 'text-color-secondary', diff: null }
      }
    })()

    return (
      <>
        <span className="p-column-title">Amount Due</span>
        <div className="flex flex-column gap-2">
          <div>{amountDue} {paymentCurrency}</div>
          <div className={classNames('flex align-items-center gap-2 text-sm', colorClass)}>
            <i className={icon} />
            <div className="flex flex-column">
              <span>{label}</span>
              {diff != null && <span className="text-xs">({diff})</span>}
            </div>
          </div>
        </div>
      </>
    )
  }

  const lineItemsBodyTemplate = (rowData: EnrichedContract) => {
    if (rowData.lineItems.length === 0) return null

    return (
      <>
        <span className="p-column-title">Line Items</span>
        <div className="flex flex-column gap-1">
          {rowData.lineItems.map((lineItem, index) => {
            const product = productMap.get(lineItem.productId)
            const productName = product?.name ?? '(Unknown Product)'
            const associatedObjectCount = lineItem.associatedObjects.length
            const isFullyAssociated = associatedObjectCount === lineItem.quantity

            return (
              <span key={index} className={classNames('text-sm flex align-items-start gap-1', { 'text-green-500': isFullyAssociated })}>
                <i className="pi pi-fw pi-box flex-none" style={{ marginTop: '0.1rem' }} />
                {productName} &times;{lineItem.quantity}
              </span>
            )
          })}
        </div>
      </>
    )
  }

  const shippingRecordsBodyTemplate = (rowData: EnrichedContract) => {
    if (rowData.shippingRecords.length === 0) return null

    const shippingTypeConfigs: Record<string, { icon: string; label: string }> = {
      TO_WAREHOUSE: { icon: 'pi-warehouse', label: 'To Warehouse' },
      TO_CUSTOMER: { icon: 'pi-truck', label: 'To Customer' },
    }

    const shippingStatusConfigs: Record<string, { label: string; className: string }> = {
      PENDING: { label: 'Pending', className: 'text-color-secondary' },
      TRACKING: { label: 'Tracking', className: 'text-blue-500' },
      DELIVERED: { label: 'Delivered', className: 'text-green-500' },
      RETURNED: { label: 'Returned', className: 'text-orange-500' },
      CANCELLED: { label: 'Cancelled', className: 'text-orange-500' },
      OTHER: { label: 'Other', className: 'text-orange-500' },
    }

    return (
      <>
        <span className="p-column-title">Shipping Records</span>
        <div className="flex flex-column gap-2">
          {rowData.shippingRecords.map((record: ShippingRecordResBodyDto, index: number) => {
            const shippingTypeConfig = shippingTypeConfigs[record.shippingType]
            const shippingStatusConfig = shippingStatusConfigs[record.shippingStatus]

            return (
              <div key={index} className={classNames('text-sm flex align-items-center gap-2', shippingStatusConfig?.className)}>
                <i className={classNames('pi pi-fw', shippingTypeConfig?.icon ?? 'pi-box')} title={shippingTypeConfig?.label ?? record.shippingType} />
                <div className="flex flex-column">
                  <span>{shippingStatusConfig?.label ?? record.shippingStatus}</span>
                  {record.shippingStatus === 'DELIVERED' && record.deliveredTime != null && (
                    <span className="text-xs">({formatUTCDate(new Date(record.deliveredTime))})</span>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </>
    )
  }

  const actionBodyTemplate = (rowData: EnrichedContract) => (
    <div className="actions flex flex-wrap gap-2">
      <Button tooltip="edit" icon="pi pi-pencil" className="p-button-rounded p-button-success" onClick={() => openEditDialog(rowData)} />
      <Button tooltip="delete" icon="pi pi-trash" className="p-button-rounded p-button-danger" onClick={() => openDeleteDialog(rowData)} />
    </div>
  )

  // --- Header & Footers ---

  const leftToolbarTemplate = () => (
    <div className="my-2">
      <Button label="New" icon="pi pi-plus" className="p-button-success mr-2" onClick={openCreateDialog} />
    </div>
  )

  const header = (
    <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center">
      <h5 className="m-0">Manage Contracts</h5>
      <IconField iconPosition="left" className="block mt-2 md:mt-0">
        <InputIcon className="pi pi-fw pi-search" />
        <InputText type="search" onChange={(event) => setGlobalFilter(event.target.value)} placeholder="Search..." />
      </IconField>
    </div>
  )

  const houseTreeSelectValueTemplate = (selectedNodes: any, onRemove?: (houseId: string) => void) => {
    const houseNodes = (selectedNodes ?? []).filter((node: any) => node.key.startsWith('house/'))

    if (houseNodes.length === 0) return (
      <span>Select houses...</span>
    )

    return (
      <div className="flex flex-wrap gap-1">
        {houseNodes.map((houseNode: any) => {
          const houseId = houseNode.key.split('/')[1]

          return (
            <span key={houseNode.key} className="p-treeselect-token mr-0">
              {/* Main Label */}
              <span className="p-treeselect-token-label flex align-items-center gap-1">
                {(houseChipLabelMap.get(houseId) ?? [houseNode.label]).map((segment, index) => (
                  <span className="flex align-items-center gap-1" key={index}>
                    {index > 0 && <i className="pi pi-angle-right" />}
                    <span>{segment}</span>
                  </span>
                ))}
              </span>

              {/* Delete Icon */}
              <span
                className="p-treeselect-token-icon pi pi-times-circle ml-2 cursor-pointer"
                onClick={(event) => {
                  event.stopPropagation()
                  onRemove?.(houseId)
                }}
              />
            </span>
          )
        })}
      </div>
    )
  }

  const isMutating = createContractMutation.isLoading || patchContractMutation.isLoading || deleteContractMutation.isLoading
  const isLoading = listContractsQuery.isLoading || listCompaniesQuery.isLoading || listUsersQuery.isLoading || listProductsQuery.isLoading || listScalesQuery.isLoading || listDevicesQuery.isLoading || listWaterMetersQuery.isLoading

  const createOrEditDialogFooter = (
    <>
      <Button label="Cancel" icon="pi pi-times" className="p-button-secondary p-button-text" onClick={hideCreateOrEditDialog} disabled={isMutating} />
      <Button label="Save" icon="pi pi-save" className="p-button-text" onClick={isCreateMode ? createContract : editContract} disabled={isMutating} />
    </>
  )

  const deleteDialogFooter = (
    <>
      <Button label="No" icon="pi pi-times" className="p-button-text" onClick={hideDeleteDialog} disabled={isMutating} />
      <Button label="Yes" icon="pi pi-check" className="p-button-text" onClick={deleteContract} disabled={isMutating} />
    </>
  )

  // --- Render ---

  const activationDateCalValue = focusedContract.activationDate ? utcToCalendarValue(new Date(focusedContract.activationDate)) : null
  const expirationDateCalValue = focusedContract.expirationDate ? utcToCalendarValue(new Date(focusedContract.expirationDate)) : null
  const todayCalValue = utcToCalendarValue(nowTruncatedToDay())
  const nowMinuteCalValue = utcToCalendarValue(nowTruncatedToMinute())

  return (
    <div className="grid crud-demo">
      <div className="col-12">
        <div className="card">
          <Toolbar className="mb-4" left={leftToolbarTemplate} />

          {isLoading ? (
            <DataTable value={[...Array(10)]} className="p-datatable-striped" header={header}>
              <Column header="Invoice Number" style={{ minWidth: '14rem' }} body={<Skeleton />} />
              <Column header="Line Items" style={{ minWidth: '14rem' }} body={<Skeleton />} />
              <Column header="Activation Date" style={{ minWidth: '10rem' }} body={<Skeleton />} />
              <Column header="Expiration Date" style={{ minWidth: '10rem' }} body={<Skeleton />} />
              <Column header="Amount Due" style={{ minWidth: '10rem' }} body={<Skeleton />} />
              <Column header="Shipping Records" style={{ minWidth: '10rem' }} body={<Skeleton />} />
              <Column header="Action" style={{ minWidth: '8rem' }} body={<Skeleton />} />
            </DataTable>
          ) : (
            <DataTable
              value={enrichedContracts}
              dataKey="id"
              paginator
              rows={10}
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              className="datatable-responsive"
              paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
              currentPageReportTemplate="Showing {first} to {last} of {totalRecords} contracts"
              globalFilter={globalFilter}
              globalFilterFields={['invoiceNo', 'companyName', 'businessName', 'creatorName']}
              emptyMessage="No contracts found."
              header={header}
              responsiveLayout="scroll"
              rowClassName={(rowData) => ({ 'opacity-50': rowData.isResolved })}
            >
              <Column header="Invoice Number" field="invoiceNo" sortable body={invoiceNumberBodyTemplate} headerStyle={{ minWidth: '14rem' }} />
              <Column header="Line Items" body={lineItemsBodyTemplate} headerStyle={{ minWidth: '14rem' }} />
              <Column header="Activation Date" field="activationDate" sortable body={activationDateBodyTemplate} headerStyle={{ minWidth: '10rem' }} />
              <Column header="Expiration Date" field="expirationDate" sortable body={expirationDateBodyTemplate} headerStyle={{ minWidth: '10rem' }} />
              <Column header="Amount Due" body={paymentBodyTemplate} headerStyle={{ minWidth: '10rem' }} />
              <Column header="Shipping Records" body={shippingRecordsBodyTemplate} headerStyle={{ minWidth: '10rem' }} />
              <Column header="Action" body={actionBodyTemplate} headerStyle={{ minWidth: '8rem' }} />
            </DataTable>
          )}

          {/* Create / Edit Dialog */}
          <Dialog
            blockScroll
            visible={createOrEditDialogVisible}
            style={{ width: '600px', height: '100vh' }}
            header={isCreateMode ? 'Create new contract' : 'Edit contract'}
            modal
            className="p-fluid"
            footer={createOrEditDialogFooter}
            onHide={hideCreateOrEditDialog}
          >
            <TabView panelContainerStyle={{ paddingLeft: 0, paddingRight: 0 }}>
              <TabPanel header="General">
                <div className="formgrid grid">
                  <div className="field col">
                    <label htmlFor="companyId">Company *</label>
                    <Dropdown
                      id="companyId"
                      value={focusedContract.companyId}
                      options={companyDropdownOptions}
                      filter
                      onChange={(event) => setFocusedContract((prev) => ({ ...prev, companyId: event.value, businessId: null }))}
                      disabled={isMutating}
                    />
                    {!focusedContract.companyId && <small className="p-invalid" style={{ color: 'red' }}>Required.</small>}
                  </div>

                  <div className="field col">
                    <label htmlFor="businessId">Business</label>
                    <Dropdown
                      id="businessId"
                      value={focusedContract.businessId}
                      options={businessDropdownOptions}
                      onChange={(event) => setFocusedContract((prev) => ({ ...prev, businessId: event.value }))}
                      disabled={isMutating || !focusedContract.companyId}
                    />
                  </div>
                </div>

                <div className="formgrid grid">
                  <div className="field col">
                    <label htmlFor="quoteNo">Quote No.</label>
                    <InputText
                      id="quoteNo"
                      value={focusedContract.quoteNo || ''}
                      onChange={(event) => setFocusedContract((prev) => ({ ...prev, quoteNo: event.target.value || null }))}
                      disabled={isMutating}
                    />
                  </div>

                  <div className="field col">
                    <label htmlFor="quoteUrl">Quote URL</label>
                    <InputText
                      id="quoteUrl"
                      value={focusedContract.quoteUrl || ''}
                      onChange={(event) => setFocusedContract((prev) => ({ ...prev, quoteUrl: event.target.value || null }))}
                      disabled={isMutating}
                    />
                  </div>
                </div>

                <div className="formgrid grid">
                  <div className="field col">
                    <label htmlFor="invoiceNo">Invoice No.</label>
                    <InputText
                      id="invoiceNo"
                      value={focusedContract.invoiceNo || ''}
                      onChange={(event) => setFocusedContract((prev) => ({ ...prev, invoiceNo: event.target.value || null }))}
                      disabled={isMutating}
                    />
                  </div>

                  <div className="field col">
                    <label htmlFor="invoiceUrl">Invoice URL</label>
                    <InputText
                      id="invoiceUrl"
                      value={focusedContract.invoiceUrl || ''}
                      onChange={(event) => setFocusedContract((prev) => ({ ...prev, invoiceUrl: event.target.value || null }))}
                      disabled={isMutating}
                    />
                  </div>
                </div>

                <div className="formgrid grid">
                  <div className="field col">
                    <label htmlFor="amountDue">Amount Due</label>
                    <InputNumber
                      id="amountDue"
                      value={focusedContract.amountDue}
                      onValueChange={(event) => setFocusedContract((prev) => ({ ...prev, amountDue: event.value ?? null }))}
                      disabled={isMutating}
                    />
                    {focusedContract.amountDue == null && <small className="p-invalid" style={{ color: 'red' }}>Required.</small>}
                  </div>
                  <div className="field col">
                    <label htmlFor="amountPaid">Amount Paid</label>
                    <InputNumber
                      id="amountPaid"
                      value={focusedContract.amountPaid}
                      onValueChange={(event) => setFocusedContract((prev) => ({ ...prev, amountPaid: event.value ?? 0 }))}
                      disabled={isMutating}
                    />
                  </div>
                  <div className="field col-fixed" style={{ width: '8rem' }}>
                    <label htmlFor="paymentCurrency">Currency</label>
                    <Dropdown
                      id="paymentCurrency"
                      value={focusedContract.paymentCurrency}
                      options={PAYMENT_CURRENCY_OPTIONS}
                      onChange={(event) => {
                        setFocusedContract((prev) => ({ ...prev, paymentCurrency: event.value }))
                        localStorage.setItem('contracts.lastPaymentCurrency', event.value)
                      }}
                      disabled={isMutating}
                    />
                  </div>
                </div>

                <hr className="my-3 border-top-1 surface-border" />

                <div className="field">
                  <label>Subscription Period</label>
                  <div className="p-inputgroup">
                    <InputNumber
                      value={focusedContract.subscriptionPeriodCount}
                      onValueChange={(event) => setFocusedContract((prev) => ({ ...prev, subscriptionPeriodCount: event.value ?? null }))}
                      disabled={isMutating}
                    />
                    <Dropdown
                      value={focusedContract.subscriptionPeriodUnit}
                      options={SUBSCRIPTION_PERIOD_UNIT_OPTIONS}
                      onChange={(event) => setFocusedContract((prev) => ({ ...prev, subscriptionPeriodUnit: event.value }))}
                      disabled={isMutating}
                      className="w-auto"
                    />
                  </div>
                </div>

                <div className="formgrid grid">
                  <div className="field col">
                    <label htmlFor="activationDate">Activation Date (UTC)</label>
                    <Calendar
                      id="activationDate"
                      value={activationDateCalValue}
                      viewDate={activationDateCalValue ?? todayCalValue}
                      onChange={(event) => setFocusedContract((prev) => ({ ...prev, activationDate: event.value != null ? calendarValueToUTC(event.value).toISOString() : null }))}
                      onTodayButtonClick={() => setFocusedContract((prev) => ({ ...prev, activationDate: nowTruncatedToDay().toISOString() }))}
                      dateFormat="yy-mm-dd"
                      showIcon
                      showButtonBar
                      disabled={isMutating}
                    />
                  </div>
                  <div className="field col">
                    <label htmlFor="expirationDate">Expiration Date (UTC)</label>
                    <Calendar
                      id="expirationDate"
                      value={expirationDateCalValue}
                      viewDate={expirationDateCalValue ?? todayCalValue}
                      onChange={(event) => setFocusedContract((prev) => ({ ...prev, expirationDate: event.value != null ? calendarValueToUTC(event.value).toISOString() : null }))}
                      onTodayButtonClick={() => setFocusedContract((prev) => ({ ...prev, expirationDate: nowTruncatedToDay().toISOString() }))}
                      dateFormat="yy-mm-dd"
                      showIcon
                      showButtonBar
                      disabled={isMutating}
                    />
                  </div>
                </div>

                <hr className="my-3 border-top-1 surface-border" />

                <div className="field">
                  <label htmlFor="memo">Memo</label>
                  <InputTextarea
                    id="memo"
                    value={focusedContract.memo || ''}
                    onChange={(event) => setFocusedContract((prev) => ({ ...prev, memo: event.target.value || null }))}
                    rows={3}
                    disabled={isMutating}
                  />
                </div>

                {!isCreateMode && (
                  <div className="field flex align-items-center gap-2">
                    <Checkbox
                      inputId="isResolved"
                      checked={focusedContract.isResolved}
                      onChange={(event) => setFocusedContract((prev) => ({ ...prev, isResolved: event.checked ?? false }))}
                      disabled={isMutating}
                    />
                    <label htmlFor="isResolved" className="mb-0">Mark as Resolved</label>
                  </div>
                )}
              </TabPanel>

              {!isCreateMode && (
                <TabPanel header="Line Items">
                  {focusedContract.lineItems.length > 0 && (
                    <div className="flex flex-column gap-3">
                      {focusedContract.lineItems.map((lineItem, index) => (
                        <div key={lineItem._formKey} className="border-1 surface-border border-round p-3">
                          <div className="flex gap-3">
                            <div className="flex-grow-1">
                              <div className="formgrid grid">
                                <div className="field col">
                                  <label>Product *</label>
                                  <Dropdown
                                    value={lineItem.productId}
                                    options={productDropdownOptions}
                                    filter
                                    onChange={(event) => setFocusedContract(Immer.produce((draft) => {
                                      draft.lineItems[index].productId = event.value
                                    }))}
                                    placeholder="Select a product"
                                    disabled={isMutating}
                                  />
                                  {!lineItem.productId && <small className="p-invalid" style={{ color: 'red' }}>Product is required.</small>}
                                </div>

                                <div className="field col-fixed" style={{ width: '6rem' }}>
                                  <label>Qty *</label>
                                  <InputNumber
                                    value={lineItem.quantity}
                                    min={1}
                                    onValueChange={(event) => setFocusedContract(Immer.produce((draft) => {
                                      draft.lineItems[index].quantity = event.value ?? null
                                    }))}
                                    disabled={isMutating}
                                  />
                                </div>
                              </div>

                              {lineItem.memo != null && (
                                <div className="field">
                                  <label>Memo</label>
                                  <div className="flex align-items-start gap-2">
                                    <InputTextarea
                                      value={lineItem.memo}
                                      onChange={(event) => setFocusedContract(Immer.produce((draft) => {
                                        draft.lineItems[index].memo = event.target.value
                                      }))}
                                      rows={1}
                                      autoResize
                                      disabled={isMutating}
                                      className="flex-1"
                                    />
                                    <Button
                                      icon="pi pi-times"
                                      className="p-button-danger p-button-text p-button-rounded p-button-sm flex-none"
                                      tooltip="Remove memo"
                                      onClick={() => setFocusedContract(Immer.produce((draft) => {
                                        draft.lineItems[index].memo = null
                                      }))}
                                      disabled={isMutating}
                                    />
                                  </div>
                                </div>
                              )}
                            </div>

                            <div className="flex flex-column align-items-center justify-content-center">
                              <Button
                                icon="pi pi-trash"
                                className="p-button-danger p-button-text p-button-rounded p-button-sm"
                                tooltip="Remove"
                                onClick={() => setFocusedContract(Immer.produce((draft) => {
                                  draft.lineItems.splice(index, 1)
                                }))}
                                disabled={isMutating}
                              />
                              {lineItem.memo == null && (
                                <Button
                                  icon="pi pi-pen-to-square"
                                  className="p-button-text p-button-rounded p-button-sm"
                                  tooltip="Add memo"
                                  onClick={() => setFocusedContract(Immer.produce((draft) => {
                                    draft.lineItems[index].memo = ''
                                  }))}
                                  disabled={isMutating}
                                />
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <Button
                    label="Add Line Item"
                    icon="pi pi-plus"
                    className="p-button-success p-button-outlined p-button-sm mt-3"
                    onClick={() => setFocusedContract(Immer.produce((draft) => {
                      draft.lineItems.push({
                        _formKey: generateFormKey(),
                        productId: null,
                        quantity: null,
                        associatedHouseIds: [],
                        associatedScaleIds: [],
                        associatedDeviceIds: [],
                        associatedWaterMeterIds: [],
                        memo: null,
                      })
                    }))}
                    disabled={isMutating}
                  />
                </TabPanel>
              )}

              {!isCreateMode && (
                <TabPanel header="Associated Objects">
                  {focusedContract.lineItems.length === 0 ? (
                    <p className="flex align-items-center justify-content-center text-color-secondary">
                      <i className="pi pi-info-circle mr-2" />
                      <span>Add some line items first to manage associated objects.</span>
                    </p>
                  ) : (
                    <div className="flex flex-column gap-4">
                      {focusedContract.lineItems.map((lineItem, index) => {
                        const product = lineItem.productId != null ? productMap.get(lineItem.productId) : undefined

                        return (
                          <div key={lineItem._formKey} className="border-1 surface-border border-round p-3">
                            {/* Line Item 摘要（唯讀） */}
                            <div>
                              <div className="flex align-items-center gap-2 mb-3">
                                <i className="pi pi-fw pi-box" />
                                <span className="font-semibold">{product?.name ?? '(Unknown Product)'}</span>
                                <span>&times;{lineItem.quantity ?? '?'}</span>
                              </div>
                              {lineItem.memo != null && (
                                <div className="flex text-sm text-color-secondary mt-0 mb-3 gap-2">
                                  <i className="pi pi-fw pi-pen-to-square flex-none" />
                                  <span style={{ whiteSpace: 'pre-line' }}>{lineItem.memo}</span>
                                </div>
                              )}
                            </div>

                            {/* Houses — TreeSelect (AI_EYE_SERVICE) */}
                            {(product != null && ['AI_EYE_SERVICE'].includes(product.type)) && (
                              <div className="field">
                                <label>Houses</label>
                                <TreeSelect
                                  value={buildTreeSelectionValue(listCompaniesQuery.data ?? [], lineItem.associatedHouseIds)}
                                  options={houseTreeOptions}
                                  selectionMode="checkbox"
                                  display="chip"
                                  filter
                                  placeholder="Select houses..."
                                  className="w-full"
                                  valueTemplate={(selectedNodes: any) => houseTreeSelectValueTemplate(selectedNodes, (houseId) => {
                                    const selectedHouseIdsAfterRemove = focusedContract.lineItems[index].associatedHouseIds.filter((id) => id !== houseId)

                                    setFocusedContract(Immer.produce((draft) => {
                                      draft.lineItems[index].associatedHouseIds = selectedHouseIdsAfterRemove
                                    }))
                                  })}
                                  onChange={(event) => {
                                    const selectedHouseIds = Object.entries(event.value ?? {})
                                      .filter(([nodeKey, nodeState]) => nodeKey.startsWith('house/') && nodeState.checked)
                                      .map(([nodeKey]) => nodeKey.split('/')[1])

                                    setFocusedContract(Immer.produce((draft) => {
                                      draft.lineItems[index].associatedHouseIds = selectedHouseIds
                                    }))
                                  }}
                                  disabled={isMutating}
                                />
                              </div>
                            )}

                            {/* Scales — MultiSelect (AI_EYE_OTP, AI_EYE_LEASING) */}
                            {(product != null && ['AI_EYE_OTP', 'AI_EYE_LEASING'].includes(product.type)) && (
                              <div className="field">
                                <label>Scales</label>
                                <MultiSelect
                                  value={lineItem.associatedScaleIds}
                                  options={scaleMultiSelectOptions}
                                  display="chip"
                                  filter
                                  showSelectAll={false}
                                  placeholder="Select scales..."
                                  className="w-full"
                                  pt={MONO_MULTI_SELECT_PT}
                                  onChange={(event) => {
                                    setFocusedContract(Immer.produce((draft) => {
                                      draft.lineItems[index].associatedScaleIds = event.value
                                    }))
                                  }}
                                  disabled={isMutating}
                                />
                              </div>
                            )}

                            {/* Devices — MultiSelect (Y_SERIES_OTP, Y_SERIES_LEASING, Y_SERIES_SERVICE) */}
                            {(product != null && ['Y_SERIES_OTP', 'Y_SERIES_LEASING', 'Y_SERIES_SERVICE'].includes(product.type)) && (
                              <div className="field">
                                <label>Devices</label>
                                <MultiSelect
                                  value={lineItem.associatedDeviceIds}
                                  options={deviceMultiSelectOptions}
                                  display="chip"
                                  filter
                                  showSelectAll={false}
                                  placeholder="Select devices..."
                                  className="w-full"
                                  pt={MONO_MULTI_SELECT_PT}
                                  onChange={(event) => {
                                    setFocusedContract(Immer.produce((draft) => {
                                      draft.lineItems[index].associatedDeviceIds = event.value
                                    }))
                                  }}
                                  disabled={isMutating}
                                />
                              </div>
                            )}

                            {/* Water Meters — MultiSelect (WATER_METER_OTP, WATER_METER_LEASING, WATER_METER_SERVICE) */}
                            {(product != null && ['WATER_METER_OTP', 'WATER_METER_LEASING', 'WATER_METER_SERVICE'].includes(product.type)) && (
                              <div className="field">
                                <label>Water Meters</label>
                                <MultiSelect
                                  value={lineItem.associatedWaterMeterIds}
                                  options={waterMeterMultiSelectOptions}
                                  display="chip"
                                  filter
                                  showSelectAll={false}
                                  placeholder="Select water meters..."
                                  className="w-full"
                                  pt={MONO_MULTI_SELECT_PT}
                                  onChange={(event) => {
                                    setFocusedContract(Immer.produce((draft) => {
                                      draft.lineItems[index].associatedWaterMeterIds = event.value
                                    }))
                                  }}
                                  disabled={isMutating}
                                />
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )}
                </TabPanel>
              )}

              {!isCreateMode && (
                <TabPanel header="Shipping Records">
                  {focusedContract.shippingRecords.length > 0 && (
                    <div className="flex flex-column gap-3">
                      {focusedContract.shippingRecords.map((record, index) => {
                        const deliveredTimeCalValue = record.deliveredTime ? utcToCalendarValue(new Date(record.deliveredTime)) : null
                        return (
                        <div key={record._formKey} className="border-1 surface-border border-round p-3">
                          <div className="flex gap-3">
                            <div className="flex-grow-1">
                              <div className="formgrid grid">
                                <div className="field col">
                                  <label>Shipping Type *</label>
                                  <Dropdown
                                    value={record.shippingType}
                                    options={SHIPPING_TYPE_OPTIONS}
                                    onChange={(event) => setFocusedContract(Immer.produce((draft) => {
                                      draft.shippingRecords[index].shippingType = event.value
                                    }))}
                                    placeholder="Select type"
                                    disabled={isMutating}
                                  />
                                </div>
                              </div>

                              <div className="formgrid grid">
                                <div className="field col">
                                  <label>Provider *</label>
                                  <Dropdown
                                    value={record.shippingProviderType}
                                    options={SHIPPING_PROVIDER_TYPE_OPTIONS}
                                    onChange={(event) => setFocusedContract(Immer.produce((draft) => {
                                      draft.shippingRecords[index].shippingProviderType = event.value
                                    }))}
                                    disabled={isMutating}
                                  />
                                </div>

                                <div className="field col">
                                  <label>Tracking Number *</label>
                                  <InputText
                                    value={record.trackingNumber}
                                    onChange={(event) => setFocusedContract(Immer.produce((draft) => {
                                      draft.shippingRecords[index].trackingNumber = event.target.value
                                    }))}
                                    disabled={isMutating}
                                  />
                                </div>
                              </div>

                              <div className="formgrid grid">
                                <div className="field col">
                                  <label>Status *</label>
                                  <Dropdown
                                    value={record.shippingStatus}
                                    options={SHIPPING_STATUS_OPTIONS}
                                    onChange={(event) => setFocusedContract(Immer.produce((draft) => {
                                      draft.shippingRecords[index].shippingStatus = event.value
                                    }))}
                                    placeholder="Select status"
                                    disabled={isMutating}
                                  />
                                </div>

                                <div className="field col">
                                  <label>Delivery Time (UTC)</label>
                                  <Calendar
                                    value={deliveredTimeCalValue}
                                    viewDate={deliveredTimeCalValue ?? nowMinuteCalValue}
                                    dateFormat="yy-mm-dd"
                                    showTime
                                    hourFormat="24"
                                    showIcon
                                    showButtonBar
                                    disabled={isMutating}
                                    onChange={(event) => setFocusedContract(Immer.produce((draft) => {
                                      draft.shippingRecords[index].deliveredTime = event.value != null ? calendarValueToUTC(event.value).toISOString() : null
                                    }))}
                                    onTodayButtonClick={() => setFocusedContract(Immer.produce((draft) => {
                                      draft.shippingRecords[index].deliveredTime = nowTruncatedToMinute().toISOString()
                                    }))}
                                  />
                                </div>
                              </div>

                              {record.memo != null && (
                                <div className="field">
                                  <label>Memo</label>
                                  <div className="flex align-items-start gap-2">
                                    <InputTextarea
                                      value={record.memo}
                                      onChange={(event) => setFocusedContract(Immer.produce((draft) => {
                                        draft.shippingRecords[index].memo = event.target.value
                                      }))}
                                      rows={1}
                                      autoResize
                                      disabled={isMutating}
                                      className="flex-1"
                                    />
                                    <Button
                                      icon="pi pi-times"
                                      className="p-button-danger p-button-text p-button-rounded p-button-sm flex-none"
                                      tooltip="Remove memo"
                                      onClick={() => setFocusedContract(Immer.produce((draft) => {
                                        draft.shippingRecords[index].memo = null
                                      }))}
                                      disabled={isMutating}
                                    />
                                  </div>
                                </div>
                              )}
                            </div>

                            <div className="flex flex-column align-items-center justify-content-center">
                              <Button
                                icon="pi pi-trash"
                                className="p-button-danger p-button-text p-button-rounded p-button-sm"
                                tooltip="Remove"
                                onClick={() => setFocusedContract(Immer.produce((draft) => {
                                  draft.shippingRecords.splice(index, 1)
                                }))}
                                disabled={isMutating}
                              />
                              {record.memo == null && (
                                <Button
                                  icon="pi pi-pen-to-square"
                                  className="p-button-text p-button-rounded p-button-sm"
                                  tooltip="Add memo"
                                  onClick={() => setFocusedContract(Immer.produce((draft) => {
                                    draft.shippingRecords[index].memo = ''
                                  }))}
                                  disabled={isMutating}
                                />
                              )}
                            </div>
                          </div>
                        </div>
                        )
                      })}
                    </div>
                  )}

                  <Button
                    label="Add Shipping Record"
                    icon="pi pi-plus"
                    className="p-button-success p-button-outlined p-button-sm mt-3"
                    onClick={() => setFocusedContract(Immer.produce((draft) => {
                      draft.shippingRecords.push({
                        _formKey: generateFormKey(),
                        shippingType: 'TO_CUSTOMER',
                        shippingProviderType: null,
                        trackingNumber: '',
                        shippingStatus: 'PENDING',
                        deliveredTime: null,
                        memo: null,
                      })
                    }))}
                    disabled={isMutating}
                  />
                </TabPanel>
              )}
            </TabView>
          </Dialog>

          {/* Delete Confirmation Dialog */}
          <Dialog
            blockScroll
            visible={deleteDialogVisible}
            style={{ width: '450px' }}
            header="Confirm"
            modal
            footer={deleteDialogFooter}
            onHide={hideDeleteDialog}
          >
            <div className="flex align-items-center">
              <i className="pi pi-fw pi-exclamation-triangle mr-3" style={{ fontSize: '2rem', color: 'var(--orange-500)' }} />
              <div>
                <span>Are you sure you want to delete this contract?</span>
                <ul style={{ paddingLeft: '1.2rem', marginTop: '0.5rem', marginBottom: 0 }}>
                  <li><b>{focusedContract.invoiceNo ?? '(No Invoice No.)'}</b></li>
                </ul>
              </div>
            </div>
          </Dialog>
        </div>
      </div>
    </div>
  )
}
